import '../../global/actions/initial';

import type { FC } from '../../lib/teact/teact';
import React, { memo, useEffect, useRef } from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { GlobalState } from '../../global/types';

import { DEBUG } from '../../config';
import { registerAutoNavigation } from '../../util/autoNavigationGuard';
import { getCurrentTabId } from '../../util/establishMultitabRole';
import { ACCOUNT_SLOT, getAccountSlotUrl } from '../../util/multiaccount';
import { isSlotLoggedIn } from '../../util/sessions';
import { PLATFORM_ENV } from '../../util/windowEnvironment';

import useCurrentOrPrev from '../../hooks/useCurrentOrPrev';
import useElectronDrag from '../../hooks/useElectronDrag';
import useHistoryBack from '../../hooks/useHistoryBack';

import Notifications from '../main/Notifications.async';
import Transition from '../ui/Transition';
import AuthCode from './AuthCode.async';
// import AuthPhoneNumber from './AuthPhoneNumber';
import AuthEmail from './AuthEmail';
import AuthPassword from './AuthPassword.async';
import AuthQrCode from './AuthQrCode';
import AuthRegister from './AuthRegister.async';

import './Auth.scss';

type StateProps = Pick<GlobalState, 'authState'> & {
  hasNotifications: boolean;
};

const Auth: FC<StateProps> = ({
  authState,
  hasNotifications,
}) => {
  const {
    returnToAuthPhoneNumber, goToAuthQrCode,
  } = getActions();

  const isMobile = PLATFORM_ENV === 'iOS' || PLATFORM_ENV === 'Android';

  // Check if we're trying to auth in slot 2+ when slot 1 is empty, redirect to slot 1
  useEffect(() => {
    const currentSlot = ACCOUNT_SLOT || 1;
    if (currentSlot > 1) {
      // Same rule as everywhere: a pre-login key (SPAC-2204) does not make slot 1 logged in
      if (!isSlotLoggedIn(1) && registerAutoNavigation()) {
        if (DEBUG) {
          // eslint-disable-next-line no-console
          console.log('[Auth] Slot 1 is empty, redirecting from slot', currentSlot, 'to slot 1');
        }
        // Redirect to the login screen of slot 1. `#login` is required: otherwise `App.tsx` on slot 1
        // redirects back to a slot that has only a pre-login key, and they bounce forever (SPAC-2771)
        window.location.href = getAccountSlotUrl(1, true);
      }
    }
  }, []);

  const handleChangeAuthorizationMethod = () => {
    if (!isMobile) {
      goToAuthQrCode();
    } else {
      returnToAuthPhoneNumber();
    }
  };

  useHistoryBack({
    isActive: (!isMobile && authState === 'authorizationStateWaitPhoneNumber')
      || (isMobile && authState === 'authorizationStateWaitQrCode'),
    onBack: handleChangeAuthorizationMethod,
  });

  // eslint-disable-next-line no-null/no-null
  const containerRef = useRef<HTMLDivElement>(null);
  useElectronDrag(containerRef);

  // For animation purposes
  const renderingAuthState = useCurrentOrPrev(
    authState !== 'authorizationStateReady' ? authState : undefined,
    true,
  );

  function getScreen() {
    switch (renderingAuthState) {
      case 'authorizationStateWaitCode':
        return <AuthCode />;
      case 'authorizationStateWaitPassword':
        return <AuthPassword />;
      case 'authorizationStateWaitRegistration':
        return <AuthRegister />;
      case 'authorizationStateWaitPhoneNumber':
        return <AuthEmail />;
      case 'authorizationStateWaitQrCode':
        return <AuthQrCode />;
      default:
        return <AuthEmail />;
        // return isMobile ? <AuthEmail /> : <AuthQrCode />;
    }
  }

  function getActiveKey() {
    switch (renderingAuthState) {
      case 'authorizationStateWaitCode':
        return 0;
      case 'authorizationStateWaitPassword':
        return 1;
      case 'authorizationStateWaitRegistration':
        return 2;
      case 'authorizationStateWaitPhoneNumber':
        return 3;
      case 'authorizationStateWaitQrCode':
        return 4;
      default:
        return 3;
    }
  }

  return (
    <>
      <Transition activeKey={getActiveKey()} name="fade" className="Auth" ref={containerRef}>
        {getScreen()}
      </Transition>
      <Notifications isOpen={hasNotifications} />
    </>
  );
};

export default memo(withGlobal(
  (global): StateProps => {
    const currentTabId = getCurrentTabId();
    const notifications = global.byTabId[currentTabId]?.notifications || [];

    return {
      authState: global.authState,
      hasNotifications: Boolean(notifications.length),
    };
  },
)(Auth));
